/**
 * QAIMT5ReadyPage - MT5 Setup Success Page
 * Route: /q-ai/mt5-ready
 *
 * Displayed after MT5-only user signup when QBridge is connected.
 * Guides user to attach Q.AI Panel EA to their MT5 chart.
 *
 * Key Points:
 * - NO web chat shown (MT5 users chat on MT5 chart)
 * - Single action: "Drag Q.AI Panel to your chart"
 * - Real-time status from QBridge /install-info
 * - Account binding validation (one MT5 per user)
 *
 * v1.0 - Initial implementation based on QBridge Team Handoff
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  Chip,
  Collapse,
  IconButton
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import LinkIcon from '@mui/icons-material/Link';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LanguageIcon from '@mui/icons-material/Language';
import QAIHeader from '../../Components/QAI/QAIHeader';
import QBridgeHelpModal from '../../Components/QAI/QBridgeHelpModal';
import { getQBridgeStatus } from '../../services/qbridge.service';

// ═══════════════════════════════════════════════════════════════════════════════
// BRAND CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════════
const BRAND = {
  primary: '#27B56D',
  primaryLight: '#22C55E',
  background: '#0A0A0F',
  surface: '#12121A',
  surfaceHover: '#1a1a24',
  text: '#FFFFFF',
  textSecondary: '#E2E8F0',
  textMuted: '#94A3B8',
  textDim: '#64748B',
  border: 'rgba(255, 255, 255, 0.08)',
  borderLight: 'rgba(255, 255, 255, 0.15)',
  success: '#22C55E',
  warning: '#F59E0B',
  error: '#EF4444',
  cyan: '#3DEAFF'
};

// ═══════════════════════════════════════════════════════════════════════════════
// STATUS INDICATOR COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const StatusIndicator = ({ connected, label, value, subValue }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      p: 1.5,
      backgroundColor: connected ? 'rgba(34, 197, 94, 0.08)' : 'rgba(245, 158, 11, 0.08)',
      borderRadius: '10px',
      border: `1px solid ${connected ? 'rgba(34, 197, 94, 0.2)' : 'rgba(245, 158, 11, 0.2)'}`
    }}
  >
    {connected ? (
      <CheckCircleIcon sx={{ color: BRAND.success, fontSize: 24 }} />
    ) : (
      <RadioButtonUncheckedIcon sx={{ color: BRAND.warning, fontSize: 24 }} />
    )}
    <Box sx={{ flex: 1 }}>
      <Typography sx={{ color: BRAND.textSecondary, fontSize: '0.875rem', fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography sx={{ color: BRAND.text, fontSize: '1rem', fontWeight: 700 }}>
        {value}
      </Typography>
      {subValue && (
        <Typography sx={{ color: BRAND.textMuted, fontSize: '0.75rem' }}>
          {subValue}
        </Typography>
      )}
    </Box>
  </Box>
);

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const QAIMT5ReadyPage = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTroubleshooting, setShowTroubleshooting] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Determine account type from localStorage or status
  const qaiUser = JSON.parse(localStorage.getItem('qai_user') || '{}');
  const accountType = qaiUser?.accountType || 'mt5'; // Default to mt5 for this page

  // Fetch QBridge status
  const fetchStatus = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch('http://localhost:5556/install-info');
      if (!response.ok) throw new Error('QBridge not responding');

      const data = await response.json();
      console.log('[MT5ReadyPage] QBridge status:', data);
      setStatus(data);

      // Auto-advance steps based on status
      if (data.qbridge?.running) {
        if (data.mt5?.connected) {
          if (data.auth?.authorized) {
            setActiveStep(2); // All ready - show "Attach EA" step
          } else {
            setActiveStep(1); // MT5 connected but not authed
          }
        } else {
          setActiveStep(0); // QBridge running but MT5 not connected
        }
      }
    } catch (err) {
      console.error('[MT5ReadyPage] Error fetching status:', err);
      setError('Could not connect to QBridge. Make sure it is running.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();

    // Poll every 5 seconds for status updates
    const interval = setInterval(fetchStatus, 5000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  // Check if user has valid token
  useEffect(() => {
    const token = localStorage.getItem('qai_token');
    if (!token) {
      navigate('/q-ai/signup');
    }
  }, [navigate]);

  const handleRefresh = () => {
    fetchStatus();
  };

  const handleGoToWebDashboard = () => {
    navigate('/q-ai/coach');
  };

  const handleTryProtocolHandler = () => {
    window.location.href = 'qbridge://start';
    // Wait and refresh
    setTimeout(fetchStatus, 3000);
  };

  const isAllReady = status?.qbridge?.running && status?.mt5?.connected && status?.auth?.authorized;

  return (
    <Box
      sx={{
        backgroundColor: BRAND.background,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background Glow */}
      <Box
        sx={{
          position: 'absolute',
          top: '5%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '800px',
          height: '500px',
          background: `radial-gradient(ellipse, rgba(39, 181, 109, 0.06) 0%, transparent 60%)`,
          pointerEvents: 'none'
        }}
      />

      {/* Shared Header with Navigation */}
      <QAIHeader
        accountType={accountType}
        balance={status?.auth?.balance}
        onHelp={() => setShowHelpModal(true)}
        currentPage="mt5-setup"
        qbridgeStatus={{
          connected: status?.mt5?.connected || false,
          qbridgeRunning: status?.qbridge?.running || false,
          mt5Detected: status?.mt5?.connected || false,
          mt5Account: status?.mt5?.account,
          mt5Broker: status?.mt5?.broker
        }}
        showNavigation={true}
      />

      {/* Sub-header with Page Title and Refresh */}
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${BRAND.border}`,
          backgroundColor: BRAND.surface,
          position: 'relative',
          zIndex: 1
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Chip
            label="MT5 Setup"
            size="small"
            sx={{
              backgroundColor: 'rgba(39, 181, 109, 0.15)',
              color: BRAND.primary,
              fontWeight: 600,
              fontSize: '0.75rem'
            }}
          />
          {status?.auth?.email && (
            <Typography sx={{ color: BRAND.textMuted, fontSize: '0.875rem' }}>
              {status.auth.email}
            </Typography>
          )}
        </Box>
        <Button
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={isLoading}
          size="small"
          sx={{
            color: BRAND.textMuted,
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { color: BRAND.text }
          }}
        >
          {isLoading ? 'Checking...' : 'Refresh Status'}
        </Button>
      </Box>

      {/* Main Content */}
      <Container
        maxWidth="md"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          py: { xs: 3, sm: 4 },
          px: { xs: 2, sm: 3 },
          position: 'relative',
          zIndex: 1
        }}
      >
        {/* Success Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          {isAllReady ? (
            <>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(34, 197, 94, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 48, color: BRAND.success }} />
              </Box>
              <Typography
                sx={{
                  color: BRAND.success,
                  fontSize: { xs: '1.75rem', sm: '2rem' },
                  fontWeight: 800,
                  mb: 1
                }}
              >
                Q.AI for MT5 is Ready!
              </Typography>
              <Typography sx={{ color: BRAND.textSecondary, fontSize: '1.125rem' }}>
                One more step: Attach the Q.AI Panel to your chart
              </Typography>
            </>
          ) : (
            <>
              <Typography
                sx={{
                  color: BRAND.text,
                  fontSize: { xs: '1.5rem', sm: '1.75rem' },
                  fontWeight: 800,
                  mb: 1
                }}
              >
                Setting Up Q.AI for MT5
              </Typography>
              <Typography sx={{ color: BRAND.textSecondary, fontSize: '1rem' }}>
                Please wait while we connect to your MT5 platform
              </Typography>
            </>
          )}
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert
            severity="warning"
            sx={{
              mb: 3,
              backgroundColor: 'rgba(245, 158, 11, 0.1)',
              color: BRAND.warning,
              borderRadius: '10px',
              border: '1px solid rgba(245, 158, 11, 0.2)',
              '& .MuiAlert-icon': { color: BRAND.warning }
            }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={handleTryProtocolHandler}
                sx={{ fontWeight: 600 }}
              >
                Launch QBridge
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {/* Connection Status Grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: 2,
            mb: 4
          }}
        >
          <StatusIndicator
            connected={status?.qbridge?.running}
            label="QBridge"
            value={status?.qbridge?.running ? `v${status.qbridge.version}` : 'Not Running'}
            subValue={status?.qbridge?.running ? `Port ${status.qbridge.port}` : null}
          />
          <StatusIndicator
            connected={status?.mt5?.connected}
            label="MT5 Platform"
            value={status?.mt5?.connected ? status.mt5.broker : 'Not Connected'}
            subValue={status?.mt5?.connected ? `Account: ${status.mt5.account}` : null}
          />
          <StatusIndicator
            connected={status?.auth?.authorized}
            label="Q.AI Account"
            value={status?.auth?.authorized ? status.auth.email : 'Not Linked'}
            subValue={status?.auth?.authorized ? `${status.auth.balance} tokens` : null}
          />
        </Box>

        {/* Main Instruction Card - Only show when ready */}
        {isAllReady && (
          <Box
            sx={{
              backgroundColor: BRAND.surface,
              borderRadius: '16px',
              border: `2px solid ${BRAND.primary}`,
              p: { xs: 2.5, sm: 3 },
              mb: 3,
              boxShadow: '0 0 40px rgba(39, 181, 109, 0.1)'
            }}
          >
            <Typography
              sx={{
                color: BRAND.primary,
                fontSize: '1.25rem',
                fontWeight: 700,
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <DesktopWindowsIcon /> Final Step: Attach Q.AI Panel to Chart
            </Typography>

            <Stepper activeStep={-1} orientation="vertical" sx={{ mt: 2 }}>
              <Step expanded>
                <StepLabel
                  StepIconComponent={() => (
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        backgroundColor: BRAND.primary,
                        color: '#000',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: '0.875rem'
                      }}
                    >
                      1
                    </Box>
                  )}
                >
                  <Typography sx={{ color: BRAND.text, fontWeight: 600, fontSize: '1rem' }}>
                    Open MT5 Platform
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Typography sx={{ color: BRAND.textMuted, fontSize: '0.9375rem', mb: 1 }}>
                    Launch MetaTrader 5 if not already open
                  </Typography>
                </StepContent>
              </Step>

              <Step expanded>
                <StepLabel
                  StepIconComponent={() => (
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        backgroundColor: BRAND.primary,
                        color: '#000',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: '0.875rem'
                      }}
                    >
                      2
                    </Box>
                  )}
                >
                  <Typography sx={{ color: BRAND.text, fontWeight: 600, fontSize: '1rem' }}>
                    Find Q.AI Panel in Navigator
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Typography sx={{ color: BRAND.textMuted, fontSize: '0.9375rem', mb: 1 }}>
                    Press <strong>Ctrl+N</strong> to open Navigator, then go to:
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: 'rgba(0, 0, 0, 0.3)',
                      borderRadius: '8px',
                      p: 1.5,
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      color: BRAND.cyan
                    }}
                  >
                    Navigator → Expert Advisors → QAI_Panel
                  </Box>
                </StepContent>
              </Step>

              <Step expanded>
                <StepLabel
                  StepIconComponent={() => (
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        backgroundColor: BRAND.primary,
                        color: '#000',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: '0.875rem'
                      }}
                    >
                      3
                    </Box>
                  )}
                >
                  <Typography sx={{ color: BRAND.text, fontWeight: 600, fontSize: '1rem' }}>
                    Drag to Any Chart
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Typography sx={{ color: BRAND.textMuted, fontSize: '0.9375rem', mb: 1 }}>
                    Drag <strong>QAI_Panel</strong> onto any chart window. The Q.AI chat panel will appear!
                  </Typography>
                  <Box
                    sx={{
                      mt: 2,
                      p: 1.5,
                      backgroundColor: 'rgba(39, 181, 109, 0.1)',
                      borderRadius: '8px',
                      border: '1px solid rgba(39, 181, 109, 0.2)'
                    }}
                  >
                    <Typography sx={{ color: BRAND.primary, fontSize: '0.875rem', fontWeight: 600 }}>
                      💡 Pro Tip: You can attach Q.AI Panel to multiple charts for different symbols!
                    </Typography>
                  </Box>
                </StepContent>
              </Step>
            </Stepper>
          </Box>
        )}

        {/* Why Chat on MT5 Card */}
        <Box
          sx={{
            backgroundColor: BRAND.surface,
            borderRadius: '12px',
            border: `1px solid ${BRAND.border}`,
            p: 2.5,
            mb: 3
          }}
        >
          <Typography
            sx={{
              color: BRAND.text,
              fontSize: '1rem',
              fontWeight: 700,
              mb: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            💬 Why Chat on MT5 Instead of Web?
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {[
              'See AI insights directly on your trading chart',
              'Real-time position analysis with live data',
              'One-click trade execution from recommendations',
              'Context-aware responses based on current chart'
            ].map((item, idx) => (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon sx={{ color: BRAND.primary, fontSize: 18 }} />
                <Typography sx={{ color: BRAND.textSecondary, fontSize: '0.9375rem' }}>
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Troubleshooting Section */}
        <Box
          sx={{
            backgroundColor: BRAND.surface,
            borderRadius: '12px',
            border: `1px solid ${BRAND.border}`,
            overflow: 'hidden'
          }}
        >
          <Box
            onClick={() => setShowTroubleshooting(!showTroubleshooting)}
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              '&:hover': { backgroundColor: BRAND.surfaceHover }
            }}
          >
            <Typography sx={{ color: BRAND.textMuted, fontSize: '0.9375rem', fontWeight: 600 }}>
              Having trouble? Click for troubleshooting tips
            </Typography>
            <IconButton size="small" sx={{ color: BRAND.textMuted }}>
              {showTroubleshooting ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={showTroubleshooting}>
            <Box sx={{ p: 2, pt: 0, borderTop: `1px solid ${BRAND.border}` }}>
              <Typography sx={{ color: BRAND.textSecondary, fontSize: '0.875rem', mb: 1.5 }}>
                <strong>QBridge not running?</strong>
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={handleTryProtocolHandler}
                sx={{
                  borderColor: BRAND.borderLight,
                  color: BRAND.textSecondary,
                  textTransform: 'none',
                  mb: 2,
                  '&:hover': { borderColor: BRAND.primary, color: BRAND.primary }
                }}
              >
                Launch QBridge
              </Button>

              <Typography sx={{ color: BRAND.textSecondary, fontSize: '0.875rem', mb: 1 }}>
                <strong>Can't find QAI_Panel in Navigator?</strong>
              </Typography>
              <Typography sx={{ color: BRAND.textMuted, fontSize: '0.8125rem', mb: 2 }}>
                Re-run the QBridge installer - it will copy the EA to your MT5 folder automatically.
              </Typography>

              <Typography sx={{ color: BRAND.textSecondary, fontSize: '0.875rem', mb: 1 }}>
                <strong>EA not loading on chart?</strong>
              </Typography>
              <Typography sx={{ color: BRAND.textMuted, fontSize: '0.8125rem' }}>
                In MT5: Tools → Options → Expert Advisors → Check "Allow automated trading"
              </Typography>
            </Box>
          </Collapse>
        </Box>

        {/* Bottom Action */}
        <Box sx={{ mt: 'auto', pt: 3, textAlign: 'center' }}>
          <Typography sx={{ color: BRAND.textMuted, fontSize: '0.875rem', mb: 1.5 }}>
            Prefer to use Q.AI on the web instead?
          </Typography>
          <Button
            variant="text"
            startIcon={<LanguageIcon />}
            onClick={handleGoToWebDashboard}
            sx={{
              color: BRAND.textSecondary,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { color: BRAND.primary }
            }}
          >
            Go to Web Dashboard
          </Button>
        </Box>
      </Container>

      {/* QBridge Help Modal */}
      <QBridgeHelpModal
        open={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        onAskHelp={() => {
          setShowHelpModal(false);
          navigate('/q-ai/coach');
        }}
      />
    </Box>
  );
};

export default QAIMT5ReadyPage;
